// src/lib/tag-config-utils.ts
/**
 * Utilities for tag-based configuration filtering
 */

import type { TagAwareConfigField, TagRestrictedOption, FilteredConfigField, ConfigField } from '@/types/tag-config';
import { checkTagAccess, getUserAssignedTags } from '@/lib/access-control';
import type { Tag } from '@/lib/firebase';
import type { User } from 'firebase/auth';

/**
 * Filters configuration fields based on user's tag access
 */
export function filterConfigFieldsByTags(
  fields: TagAwareConfigField[] | undefined,
  availableTags: Tag[],
  user: User | null
): FilteredConfigField[] {
  if (!fields) return [];

  const filteredFields: FilteredConfigField[] = [];

  for (const field of fields) {
    // Check field-level access
    if (field.fieldTags && !checkTagAccess(field.fieldTags, availableTags, user)) {
      continue; // Skip this field entirely
    }

    // Create a copy of the field for filtering
    const filteredField: FilteredConfigField = { ...field };

    // Filter options for select fields
    if (field.type === 'select' && field.options) {
      const allOptions = field.options;
      const accessibleOptions = allOptions.filter(option => {
        if (!option.tags || option.tags.length === 0) {
          return true; // No tags means everyone can access
        }
        return checkTagAccess(option.tags, availableTags, user);
      });

      // Update the field with filtered options, preserving tag information for display
      filteredField.options = accessibleOptions.map(opt => ({
        label: opt.label,
        value: opt.value,
        tags: opt.tags // Preserve tags for display purposes
      }));

      // Track if options were restricted
      if (accessibleOptions.length < allOptions.length) {
        filteredField.isRestricted = true;
        filteredField.restrictedOptions = allOptions
          .filter(opt => !accessibleOptions.includes(opt))
          .map(opt => opt.value);
      }
    }

    // Apply tag-based defaults
    if (field.tagDefaults) {
      for (const tagDefault of field.tagDefaults) {
        if (checkTagAccess(tagDefault.tags, availableTags, user)) {
          // User has access to this default value
          if (!filteredField.placeholder) {
            filteredField.placeholder = String(tagDefault.defaultValue);
          }
          break; // Use first matching default
        }
      }
    }

    // Note: Tag-based validation would be applied at runtime, not during filtering
    // Store it for later use
    filteredField.tagValidation = field.tagValidation;

    filteredFields.push(filteredField);
  }

  return filteredFields;
}

/**
 * Checks if a configuration value is allowed based on user's tags
 */
export function isConfigValueAllowed(
  fieldKey: string,
  value: any,
  field: TagAwareConfigField,
  availableTags: Tag[],
  user: User | null
): { allowed: boolean; reason?: string } {
  // For select fields, check if the selected option is accessible
  if (field.type === 'select' && field.options) {
    const selectedOption = field.options.find(opt => opt.value === value);
    if (!selectedOption) {
      return { allowed: false, reason: 'Invalid option selected' };
    }

    if (selectedOption.tags && selectedOption.tags.length > 0) {
      if (!checkTagAccess(selectedOption.tags, availableTags, user)) {
        return { 
          allowed: false, 
          reason: `You don't have access to the "${selectedOption.label}" option` 
        };
      }
    }
  }

  // Apply tag-based validation rules
  if (field.tagValidation) {
    for (const validation of field.tagValidation) {
      if (checkTagAccess(validation.tags, availableTags, user)) {
        // User has these tags, apply the validation
        if (field.type === 'number' && typeof value === 'number') {
          if (validation.min !== undefined && value < validation.min) {
            return { 
              allowed: false, 
              reason: validation.message || `Value must be at least ${validation.min}` 
            };
          }
          if (validation.max !== undefined && value > validation.max) {
            return { 
              allowed: false, 
              reason: validation.message || `Value must be at most ${validation.max}` 
            };
          }
        }

        if (field.type === 'text' && typeof value === 'string' && validation.pattern) {
          const regex = new RegExp(validation.pattern);
          if (!regex.test(value)) {
            return { 
              allowed: false, 
              reason: validation.message || 'Invalid format' 
            };
          }
        }
      }
    }
  }

  return { allowed: true };
}

/**
 * Common tag-restricted configurations for tools
 */
export const TAG_RESTRICTED_CONFIGS = {
  fileBrowser: {
    buckets: [
      { 
        label: 'Aitana Public Bucket', 
        value: 'aitana-public-bucket',
        description: 'Public demo content' 
      },
      { 
        label: 'Aitana Documents Bucket', 
        value: 'aitana-documents-bucket',
        tags: ['ONE', 'admin-tools'],
        description: 'Internal company documents' 
      },
      { 
        label: 'Aitana BigQuery Bucket', 
        value: 'aitana-bigquery-bucket',
        tags: ['admin-tools', 'data-team'],
        description: 'BigQuery data exports' 
      }
    ] as TagRestrictedOption[]
  },

  vertexSearch: {
    datastores: [
      { 
        label: 'Aitana Public Welcome', 
        value: 'aitana_public_welcome',
        description: 'Public knowledge base' 
      },
      { 
        label: 'Our New Energy Documents', 
        value: 'aitana3',
        tags: ['ONE', 'admin-tools'],
        description: 'Energy sector research and docs' 
      }
    ] as TagRestrictedOption[]
  },

  documentSearchAgent: {
    presets: [
      { 
        label: 'Public Demo (aitana-public-bucket + public welcome)', 
        value: 'public_demo',
        description: 'Public accessible content only' 
      },
      { 
        label: 'Our New Energy Documents', 
        value: 'energy_docs',
        tags: ['ONE', 'admin-tools'],
        description: 'Energy research documents' 
      },
      { 
        label: 'Financial Analysis (finance-bucket + finance_datastore)', 
        value: 'finance_analysis',
        tags: ['finance-team', 'admin-tools'],
        description: 'Financial reports and analysis' 
      }
    ] as TagRestrictedOption[]
  }
};

/**
 * Helper to get user's accessible tag names
 */
export function getUserTagNames(availableTags: Tag[], user: User | null): string[] {
  return availableTags
    .filter(tag => checkTagAccess([tag.id], availableTags, user))
    .map(tag => tag.name);
}